import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { isAuthenticated, getUserPrivileges, getUserRole } from '../../../utils/auth';
import api from '../../../utils/api';
import Sidebar from '../../../components/Sidebar/sidebar';
import Header from '../../../components/Header';
import './RetailPolicyProspect.css';
import { API_BASE_URL } from '../../../utils/apiBase';
import { prospectAccessPrivileges } from '../../../utils/prospectPrivileges';


const RetailPolicyProspect = () => {
    const [collapsed, setCollapsed] = useState(false);
    const userPrivileges = getUserPrivileges();
    const isAdmin = getUserRole() === 'admin';

    const RETAIL_PROSPECT_PRIVILEGES = prospectAccessPrivileges('retail');
    const CORPORATE_PROSPECT_PRIVILEGES = prospectAccessPrivileges('corporate');

    const canViewRetailProspects = isAdmin || RETAIL_PROSPECT_PRIVILEGES.some(p =>
        userPrivileges.includes(p)
    );

    const canViewCorporateProspects = isAdmin || CORPORATE_PROSPECT_PRIVILEGES.some(p =>
        userPrivileges.includes(p)
    );

    const defaultClientType = canViewRetailProspects
        ? 'Retail'
        : canViewCorporateProspects
            ? 'Corporate'
            : 'Retail';

    /* ================= CLIENT TYPE ================= */
    const [clientType, setClientType] = useState(defaultClientType);

    /* ================= FORM DATA ================= */
    const [formData, setFormData] = useState({
        clientType: defaultClientType,
        prospectId: '',
        prospectName: '',
        classOfPolicyId: '',
        classOfPolicyName: '',
        policyId: '',
        policyName: '',
        typeOfBusiness: '',
        previousPolicyNumber: '',
        // grossPremium: '',
        premiumSize: '',
        tentativeStartDate: '',
        firstFollowUpDate: '',
        remarks: ''
    });

    /* ================= CLIENT SEARCH ================= */
    const [clientInput, setClientInput] = useState('');
    const [clientSuggestions, setClientSuggestions] = useState([]);
    const [clientFocused, setClientFocused] = useState(false);
    const [clientSelectedIndex, setClientSelectedIndex] = useState(-1);

    /* ================= POLICY LIST ================= */
    const [policies, setPolicies] = useState([]);

    /* ================= PREVIOUS POLICIES ================= */
    const [previousPolicies, setPreviousPolicies] = useState([]);
    const [showOlderPolicies, setShowOlderPolicies] = useState(false);
    const [showPrevDropdown, setShowPrevDropdown] = useState(false);
    /* ================= CLASS OF POLICY ================= */
    const [classPolicies, setClassPolicies] = useState([]);
    const [premiumDisplay, setPremiumDisplay] = useState('');
    const [premiumError, setPremiumError] = useState('');
    /* ================= ROLLOVER DOCUMENT ================= */
    const [rolloverDoc, setRolloverDoc] = useState(null);
    const [, setRolloverDocError] = useState('');
    const [rolloverPreviewUrl, setRolloverPreviewUrl] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [showDocPreview, setShowDocPreview] = useState(false);
    const [, setDocPreviewUrl] = useState('');
    const [showSearchHelp, setShowSearchHelp] = useState(false);

    const [categories, setCategories] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState('');

    /* ===== SEARCH INPUT STATES ===== */
    const [classInput, setClassInput] = useState('');
    const [showClassDropdown, setShowClassDropdown] = useState(false);

    const [policyInput, setPolicyInput] = useState('');
    const [showPolicyDropdown, setShowPolicyDropdown] = useState(false);


    const [insurerInput, setInsurerInput] = useState('');
    const [insurerSuggestions, setInsurerSuggestions] = useState([]);
    const [showInsurerDropdown, setShowInsurerDropdown] = useState(false);

    const [showIntermediatorModal, setShowIntermediatorModal] = useState(false);

    const initialIntermediatorState = {
        name: '',
        irdaiNumber: '',
        location: '',
        remarks: ''
    };

    const [intermediator, setIntermediator] = useState(initialIntermediatorState);

    const hasIntermediatorDetails =
        intermediator.name ||
        intermediator.irdaiNumber ||
        intermediator.location ||
        intermediator.remarks;





    const insurerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                insurerRef.current &&
                !insurerRef.current.contains(event.target)
            ) {
                // Close dropdown
                setShowInsurerDropdown(false);

                // Reset search ONLY if insurer not selected
                if (!formData.currentInsurerId) {
                    setInsurerInput('');
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [formData.currentInsurerId]);


    /* ================= FETCH CLIENTS ================= */
    useEffect(() => {
        const fetchClients = async () => {
            if (!clientFocused || clientInput.length < 1) {
                setClientSuggestions([]);
                return;
            }

            try {
                const apiUrl =
                    clientType === 'Retail'
                        ? `${API_BASE_URL}/api/prospects/search-prospects?q=${clientInput}`
                        : `${API_BASE_URL}/api/prospects/search-corporates?q=${clientInput}`;

                const res = await api.get(apiUrl);
                setClientSuggestions(res.data);
            } catch (err) {
                console.error('Error fetching prospects', err);
            }
        };

        const debounce = setTimeout(fetchClients, 300);
        return () => clearTimeout(debounce);
    }, [clientInput, clientFocused, clientType]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await axios.get(
                    `${API_BASE_URL}/api/prospects/categories`
                );
                setCategories(res.data);
            } catch (err) {
                console.error('Error fetching categories', err);
            }
        };

        fetchCategories();
    }, []);


    useEffect(() => {
        if (!selectedCategoryId) {
            setClassPolicies([]);
            return;
        }

        const fetchClassOfPolicies = async () => {
            try {
                const res = await axios.get(
                    `${API_BASE_URL}/api/prospects/class-of-policies`,
                    {
                        params: { categoryId: selectedCategoryId }
                    }
                );
                setClassPolicies(res.data);
            } catch (err) {
                console.error('Error fetching class of policy', err);
            }
        };

        fetchClassOfPolicies();
    }, [selectedCategoryId]);



    /* ================= FETCH POLICY NAMES ================= */
    useEffect(() => {
        if (!formData.classOfPolicyId) {
            setPolicies([]);
            return;
        }

        const fetchPolicies = async () => {
            try {
                const res = await axios.get(
                    `${API_BASE_URL}/api/prospects/policy-names`,
                    {
                        params: {
                            classOfPolicyId: formData.classOfPolicyId
                        }
                    }
                );
                setPolicies(res.data);
            } catch (err) {
                console.error('Error fetching policies', err);
            }
        };

        fetchPolicies();
    }, [formData.classOfPolicyId]);

    const filteredClassPolicies = classPolicies.filter(c =>
        c.classOfPolicyName.toLowerCase().includes(classInput.toLowerCase())
    );

    const filteredPolicies = policies.filter(p =>
        p.policyName.toLowerCase().includes(policyInput.toLowerCase())
    );




    /* ================= FETCH PREVIOUS POLICIES ================= */
    useEffect(() => {
        if (
            formData.typeOfBusiness !== 'RENEWAL' ||
            !formData.prospectName ||
            !formData.policyName
        ) {
            setPreviousPolicies([]);
            return;
        }

        const fetchPreviousPolicies = async () => {
            try {
                const res = await axios.get(
                    `${API_BASE_URL}/api/prospects/previous-policies`,
                    {
                        params: {
                            clientName: formData.prospectName,
                            policyName: formData.policyName, // ✅ SEND POLICY NAME
                            showOlder: showOlderPolicies
                        }
                    }
                );

                setPreviousPolicies(res.data);
            } catch (err) {
                console.error('Error fetching previous policies', err);
                setPreviousPolicies([]);
            }
        };

        fetchPreviousPolicies();
    }, [
        formData.typeOfBusiness,
        formData.prospectName,
        formData.policyName,
        showOlderPolicies
    ]);

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            previousPolicyNumber: ''
        }));
        setPreviousPolicies([]);
    }, [formData.policyName]);

    // useEffect(() => {
    //     if (formData.typeOfBusiness !== 'RENEWAL') {
    //         setShowOlderPolicies(false);
    //     }
    // }, [formData.typeOfBusiness]);


    useEffect(() => {
        setShowOlderPolicies(false);
    }, [formData.prospectName]);

    useEffect(() => {
        const close = () => setShowPrevDropdown(false);
        window.addEventListener('click', close);
        return () => window.removeEventListener('click', close);
    }, []);

    useEffect(() => {
        if (formData.typeOfBusiness !== 'ROLLOVER') {
            setRolloverDoc(null);
            setRolloverDocError('');
            setRolloverPreviewUrl('');
        }
    }, [formData.typeOfBusiness]);



    const [errors, setErrors] = useState({
        previousPolicyNumber: ''
    });

    // useEffect(() => {
    //     setErrors(prev => ({
    //         ...prev,
    //         previousPolicyNumber: ''
    //     }));
    // }, [formData.typeOfBusiness]);

    // useEffect(() => {
    //     if (formData.typeOfBusiness === 'NEW') {
    //         setFormData(prev => ({
    //             ...prev,
    //             previousPolicyNumber: ''
    //         }));
    //         setErrors(prev => ({
    //             ...prev,
    //             previousPolicyNumber: ''
    //         }));
    //     }
    // }, [formData.typeOfBusiness]);

    useEffect(() => {
        if (!formData.classOfPolicyId) {
            setPolicies([]);
            setFormData(prev => ({
                ...prev,
                policyId: '',
                policyName: ''
            }));
        }
    }, [formData.classOfPolicyId]);

    useEffect(() => {
        const close = () => setShowSearchHelp(false);
        window.addEventListener('click', close);
        return () => window.removeEventListener('click', close);
    }, []);

    useEffect(() => {
        if (!insurerInput) {
            setInsurerSuggestions([]);
            return;
        }

        const fetchInsurers = async () => {
            try {
                const res = await axios.get(
                    `${API_BASE_URL}/api/prospects/insurers/search`,
                    { params: { q: insurerInput } }
                );
                setInsurerSuggestions(res.data);
            } catch (err) {
                console.error(err);
            }
        };

        const debounce = setTimeout(fetchInsurers, 300);
        return () => clearTimeout(debounce);
    }, [insurerInput]);

    useEffect(() => {
        // Reset dependent fields when Type of Business changes
        setFormData(prev => ({
            ...prev,
            previousPolicyNumber: '',
            tentativeStartDate: '',
            firstFollowUpDate: ''
        }));

        // Reset related UI state
        setPreviousPolicies([]);
        setShowPrevDropdown(false);
        setShowOlderPolicies(false);

        // Clear validation errors
        setErrors(prev => ({
            ...prev,
            previousPolicyNumber: ''
        }));
    }, [formData.typeOfBusiness]);





    // Format number to ₹ currency (Indian system)
    const formatINR = (value) => {
        if (!value && value !== 0) return '';
        return new Intl.NumberFormat('en-IN').format(value);
    };

    const parseINR = (value) => {
        return value.replace(/[^0-9]/g, '');
    };





    const validatePreviousPolicyNumber = (value) => {
        if (formData.typeOfBusiness !== 'ROLLOVER') {
            return '';
        }

        // Optional for rollover
        if (!value) return '';

        // 1️⃣ Allowed characters only
        const allowedCharsRegex = /^[A-Z0-9/-]+$/i;
        if (!allowedCharsRegex.test(value)) {
            return 'Only letters, numbers, / and - are allowed';
        }

        // 2️⃣ Minimum length check
        if (value.length < 8) {
            return 'Policy number is too short';
        }

        // 3️⃣ Maximum length check (soft validation)
        if (value.length > 30) {
            return 'Policy number seems too long';
        }

        return '';
    };



    const calculateFirstFollowUpDate = (tentativeStartDate) => {
        if (!tentativeStartDate) return '';

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);

        const tsd = new Date(tentativeStartDate);
        tsd.setHours(0, 0, 0, 0);

        const diffTime = tsd.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        let followUpDate;

        // 🔹 Rule A: within 30 days
        if (diffDays <= 30) {
            followUpDate = tomorrow;
        }
        // 🔹 Rule B: 31–60 days
        else if (diffDays <= 60) {
            followUpDate = new Date(tsd);
            followUpDate.setDate(tsd.getDate() - 30);
        }
        // 🔹 Rule C: more than 60 days
        else {
            followUpDate = new Date(tsd);
            followUpDate.setDate(tsd.getDate() - 60);

            // safety: never before tomorrow
            if (followUpDate < tomorrow) {
                followUpDate = tomorrow;
            }
        }

        return followUpDate.toISOString().split('T')[0];
    };


    useEffect(() => {
        if (!formData.tentativeStartDate) return;

        const followUpDate = calculateFirstFollowUpDate(
            formData.tentativeStartDate
        );

        setFormData(prev => ({
            ...prev,
            firstFollowUpDate: followUpDate
        }));
    }, [formData.tentativeStartDate]);


    /* ================= SUBMIT ================= */
    const handleSubmit = async e => {
        e.preventDefault();

        if (!formData.prospectId || !formData.policyId) {
            alert('Please select Prospect and Policy');
            return;
        }

        const prevPolicyError = validatePreviousPolicyNumber(
            formData.previousPolicyNumber
        );

        if (prevPolicyError) {
            setErrors(prev => ({
                ...prev,
                previousPolicyNumber: prevPolicyError
            }));
            return;
        }

        try {
            const payload = new FormData();

            Object.keys(formData).forEach(key => {
                if (typeof formData[key] === 'object') {
                    payload.append(key, JSON.stringify(formData[key]));
                } else {
                    payload.append(key, formData[key]);
                }
            });


            if (rolloverDoc) {
                payload.append('rolloverPolicyPdf', rolloverDoc);
            }

            await api.post(
                `${API_BASE_URL}/api/prospects/create`,
                payload,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            const successLabel =
                formData.clientType === 'Corporate'
                    ? 'Corporate Policy Prospect saved successfully'
                    : 'Retail Policy Prospect saved successfully';
            alert(successLabel);

            // 🔄 Reset form
            setFormData({
                clientType: defaultClientType,
                prospectId: '',
                prospectName: '',
                classOfPolicyId: '',
                classOfPolicyName: '',
                policyId: '',
                policyName: '',
                typeOfBusiness: '',
                previousPolicyNumber: '',
                premiumSize: '',
                tentativeStartDate: '',
                firstFollowUpDate: '',
                remarks: ''
            });

            setPolicies([]);
            setPremiumDisplay('');
            setPremiumError('');
            setClientInput('');
            setClientSuggestions([]);
            setClientSelectedIndex(-1);
            setClientType(defaultClientType);
            setSelectedCategoryId('');
            setClassInput('');
            setPolicyInput('');
            setShowClassDropdown(false);
            setShowPolicyDropdown(false);
            setClassPolicies([]);
            setRolloverDoc(null);
            setRolloverPreviewUrl('');
            setIntermediator(initialIntermediatorState);
            setFormData(prev => ({
                ...prev,
                intermediatorDetails: undefined,
                currentInsurerId: '',
                currentInsurerName: ''
            }));
            setInsurerInput('');
            setInsurerSuggestions([]);
            setShowInsurerDropdown(false);
        } catch (err) {
            console.error(err);
            alert('Error saving prospect');
        }
    };


    if (!isAuthenticated()) {
        return <Navigate to="/" />;
    }

    return (
        <div className="retail-policy-prospect-container">
            <div className="sidebar">
                <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            </div>

            <div className={`retail-policy-prospect-content ${collapsed ? 'collapsed' : ''}`}>
                <div className="retail-policy-prospect-sticky-header">
                    <Header />
                </div>
                <div className="retail-policy-prospect-scroll-area">
                    <section>
                        <form onSubmit={handleSubmit} className="add-prospect-form">

                            <h2 className="form-title">Add Prospect</h2>

                            {/* ================= CLIENT TYPE ================= */}
                            <div className='add-prospect-form-row-1'>
                                <div className="add-prospect-form-group">
                                    <label className="form-label form-label-required">Client Type</label>
                                    <div style={{ display: 'flex', gap: '20px' }}>
                                        {canViewRetailProspects && (
                                            <label>
                                                <input
                                                    type="radio"
                                                    value="Retail"
                                                    checked={clientType === 'Retail'}
                                                    onChange={() => {
                                                        setClientType('Retail');
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            clientType: 'Retail'
                                                        }));
                                                    }}
                                                /> Retail
                                            </label>
                                        )}

                                        {canViewCorporateProspects && (
                                            <label>
                                                <input
                                                    type="radio"
                                                    value="Corporate"
                                                    checked={clientType === 'Corporate'}
                                                    onChange={() => {
                                                        setClientType('Corporate');
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            clientType: 'Corporate'
                                                        }));
                                                    }}
                                                /> Corporate
                                            </label>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* ================= PROSPECT NAME ================= */}
                            <div className='add-prospect-form-row-2'>
                                <div className="add-prospect-form-group" style={{ position: 'relative' }}>
                                    <div className="label-with-info">
                                        <label className="form-label form-label-required">
                                            {clientType} Prospect Name
                                        </label>

                                        <span
                                            className="label-info-icon"
                                            onClick={e => {
                                                e.stopPropagation();
                                                setShowSearchHelp(prev => !prev);
                                            }}
                                            title="Search help"
                                        >
                                            ℹ️
                                        </span>
                                    </div>
                                    <input
                                        type="text"
                                        value={clientInput}
                                        placeholder="Name, Phone, Aadhaar / GST (comma separated)"
                                        onChange={e => {
                                            setClientInput(e.target.value);
                                            setFormData(prev => ({
                                                ...prev,
                                                prospectName: e.target.value,
                                                prospectId: ''
                                            }));
                                            setClientSelectedIndex(-1);
                                        }}
                                        onFocus={() => setClientFocused(true)}
                                        onBlur={() =>
                                            setTimeout(() => {
                                                setClientSuggestions([]);
                                                setClientFocused(false);
                                            }, 200)
                                        }
                                        onKeyDown={e => {
                                            if (e.key === 'ArrowDown') {
                                                setClientSelectedIndex(prev =>
                                                    Math.min(prev + 1, clientSuggestions.length - 1)
                                                );
                                            } else if (e.key === 'ArrowUp') {
                                                setClientSelectedIndex(prev =>
                                                    Math.max(prev - 1, 0)
                                                );
                                            } else if (e.key === 'Enter' && clientSelectedIndex !== -1) {
                                                const selected = clientSuggestions[clientSelectedIndex];
                                                const name =
                                                    clientType === 'Retail'
                                                        ? selected.clientName
                                                        : (selected.companyName || selected.contactPersonName || '');

                                                setClientInput(name);
                                                setFormData(prev => ({
                                                    ...prev,
                                                    prospectId: selected._id,
                                                    prospectName: name
                                                }));
                                                setClientSuggestions([]);
                                            }
                                        }}
                                        autoComplete="off"
                                        required
                                        className="input-has-icon"
                                    />


                                    {showSearchHelp && (
                                        <div className="search-help-tooltip" onClick={e => e.stopPropagation()}>
                                            <b>How to search</b>
                                            <ul>
                                                {clientType === 'Retail' ? (
                                                    <>
                                                        <li>Name → <code>Jhon</code></li>
                                                        <li>Phone → <code>98765432 (Min → 8 no.)</code></li>
                                                        <li>Aadhaar → <code>12345 (Min → 4 no.)</code></li>
                                                        <li>Any order → <code>98765,Jhon</code></li>
                                                        <li>Aadhaar → <code>Jhon,98765,1234</code></li>
                                                    </>
                                                ) : (
                                                    <>
                                                        <li>Company → <code>ABC</code></li>
                                                        <li>Phone → <code>98765432 (Min → 8 no.)</code></li>
                                                        <li>Any Order → <code>ABC,98765</code></li>
                                                        <li>GST → <code>ABC,98765,29ABCDE (Min → 5 Digits)</code></li>
                                                    </>
                                                )}
                                            </ul>
                                        </div>
                                    )}

                                    {clientFocused && (
                                        <ul className="dropdown">
                                            {/* EXISTING SUGGESTIONS */}
                                            {clientSuggestions.map((item, idx) => (
                                                <li
                                                    key={item._id}
                                                    className={idx === clientSelectedIndex ? 'highlighted' : ''}
                                                    onMouseDown={() => {
                                                        const name =
                                                            clientType === 'Retail'
                                                                ? item.clientName
                                                                : (item.companyName || item.contactPersonName || '');

                                                        setClientInput(name);
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            prospectId: item._id,
                                                            prospectName: name
                                                        }));
                                                        setClientSuggestions([]);
                                                        setClientFocused(false);
                                                    }}
                                                >
                                                    {clientType === 'Retail' ? (
                                                        <>
                                                            <b>Name:</b> {item.clientName}<br />
                                                            <b>Phone:</b> {item.phoneNumber}<br />
                                                            <b>Aadhaar:</b> {item.aadharNumber}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <b>Company:</b> {item.companyName}<br />
                                                            <b>Contact:</b> {item.contactNo}<br />
                                                            <b>GST:</b> {item.gst}
                                                        </>
                                                    )}
                                                </li>
                                            ))}

                                            {/* ADD NEW PROSPECT LINK */}
                                            <li
                                                className="add-new-client-link"
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();

                                                    window.open(
                                                        clientType === 'Retail'
                                                            ? '/retail/clients-form'
                                                            : '/corporate/clients-form',
                                                        '_blank'
                                                    );
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    color: '#007bff',
                                                    fontWeight: 600
                                                }}
                                            >
                                                + Add New {clientType} Prospect
                                            </li>

                                        </ul>
                                    )}

                                </div>

                                <div className="add-prospect-form-group"></div>

                                {/* ================= CATEGORY ================= */}
                                <div className="add-prospect-form-group">
                                    <label className="form-label form-label-required">
                                        Category
                                    </label>

                                    <select
                                        value={selectedCategoryId}
                                        onChange={e => {
                                            const categoryId = e.target.value;

                                            setSelectedCategoryId(categoryId);

                                            // 🔄 Reset dependent fields
                                            setFormData(prev => ({
                                                ...prev,
                                                classOfPolicyId: '',
                                                classOfPolicyName: '',
                                                policyId: '',
                                                policyName: ''
                                            }));

                                            setPolicies([]);
                                        }}
                                        required
                                    >
                                        <option value="">Select Category</option>

                                        {categories.map(cat => (
                                            <option key={cat._id} value={cat._id}>
                                                {cat.categoryName}
                                            </option>
                                        ))}
                                    </select>
                                </div>



                                {/* ================= CLASS OF POLICY ================= */}
                                <div className="add-prospect-form-group dropdown-wrapper">
                                    <label className="form-label form-label-required">
                                        Class of Policy
                                    </label>

                                    <input
                                        type="text"
                                        className="form-control"
                                        value={classInput}
                                        placeholder={
                                            selectedCategoryId
                                                ? 'Search Class of Policy'
                                                : 'Select Category first'
                                        }
                                        disabled={!selectedCategoryId}
                                        onFocus={() => setShowClassDropdown(true)}
                                        onChange={e => {
                                            setClassInput(e.target.value);
                                            setShowClassDropdown(true);
                                        }}
                                        onBlur={() => setTimeout(() => setShowClassDropdown(false), 150)}
                                    />

                                    {showClassDropdown && (
                                        <ul className="dropdown-list">
                                            {filteredClassPolicies.map(c => (
                                                <li
                                                    key={c._id}
                                                    onMouseDown={() => {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            classOfPolicyId: c._id,
                                                            classOfPolicyName: c.classOfPolicyName,
                                                            policyId: '',
                                                            policyName: ''
                                                        }));
                                                        setClassInput(c.classOfPolicyName);
                                                        setShowClassDropdown(false);
                                                    }}
                                                >
                                                    {c.classOfPolicyName}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>


                                {/* ================= POLICY NAME ================= */}
                                <div className="add-prospect-form-group dropdown-wrapper">
                                    <label className="form-label form-label-required">
                                        Policy Name
                                    </label>

                                    <input
                                        type="text"
                                        className="form-control"
                                        value={policyInput}
                                        placeholder={
                                            formData.classOfPolicyId
                                                ? 'Search Policy'
                                                : 'Select Class of Policy first'
                                        }
                                        disabled={!formData.classOfPolicyId}
                                        onFocus={() => setShowPolicyDropdown(true)}
                                        onChange={e => {
                                            setPolicyInput(e.target.value);
                                            setShowPolicyDropdown(true);
                                        }}
                                        onBlur={() => setTimeout(() => setShowPolicyDropdown(false), 150)}
                                    />

                                    {showPolicyDropdown && filteredPolicies.length > 0 && (
                                        <ul className="dropdown-list">
                                            {filteredPolicies.map(p => (
                                                <li
                                                    key={p._id}
                                                    onMouseDown={() => {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            policyId: p._id,
                                                            policyName: p.policyName
                                                        }));
                                                        setPolicyInput(p.policyName);
                                                        setShowPolicyDropdown(false);
                                                    }}
                                                >
                                                    {p.policyName}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                            </div>

                            {/* ================= TYPE OF BUSINESS ================= */}
                            <div className='add-prospect-form-row-3'>
                                <div className="add-prospect-form-group">
                                    <label className="form-label form-label-required">Type of Business</label>
                                    <select
                                        value={formData.typeOfBusiness}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                typeOfBusiness: e.target.value
                                            }))
                                        }
                                        required
                                    >
                                        <option value="">Select</option>
                                        <option value="NEW">New</option>
                                        <option value="RENEWAL">I-Pro Renewal</option>
                                        <option value="ROLLOVER">Other Renewal</option>
                                    </select>
                                </div>

                                {/* ================= PREVIOUS POLICY NUMBER ================= */}
                                <div className="add-prospect-form-group" style={{ position: 'relative', overflow: 'visible' }}>

                                    {/* ================= PREVIOUS POLICY NUMBER ================= */}
                                    {formData.typeOfBusiness !== 'NEW' && (
                                        <div
                                            className="add-prospect-form-group"
                                            style={{ position: 'relative', overflow: 'visible' }}
                                        >
                                            <label className="form-label">Previous Policy Number</label>

                                            {formData.typeOfBusiness === 'RENEWAL' ? (
                                                <>
                                                    {/* Custom dropdown trigger */}
                                                    <div
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            setShowPrevDropdown(prev => !prev);
                                                        }}
                                                        style={{
                                                            border: '1px solid #ccc',
                                                            padding: '8px',
                                                            background: '#fff',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        {formData.previousPolicyNumber || 'Select Previous Policy'}
                                                    </div>

                                                    {/* Dropdown */}
                                                    {showPrevDropdown && (
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                top: '110%',
                                                                left: 0,
                                                                right: 0,
                                                                background: '#fff',
                                                                border: '1px solid #ccc',
                                                                zIndex: 99999,
                                                                maxHeight: '240px',
                                                                overflowY: 'auto'
                                                            }}
                                                            onClick={e => e.stopPropagation()}
                                                        >
                                                            {previousPolicies.map(p => (
                                                                <div
                                                                    key={p._id}
                                                                    style={{ padding: '8px', cursor: 'pointer' }}
                                                                    onClick={() => {
                                                                        const d = new Date(p.policyStartDate);
                                                                        d.setFullYear(d.getFullYear() + 1);

                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            previousPolicyNumber: p.policyNumber,
                                                                            tentativeStartDate: d
                                                                                .toISOString()
                                                                                .split('T')[0]
                                                                        }));

                                                                        setShowPrevDropdown(false);
                                                                    }}
                                                                >
                                                                    {p.policyNumber}
                                                                </div>
                                                            ))}

                                                            <div
                                                                style={{
                                                                    borderTop: '1px solid #eee',
                                                                    padding: '8px'
                                                                }}
                                                            >
                                                                <label
                                                                    style={{
                                                                        display: 'flex',
                                                                        gap: '6px',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={showOlderPolicies}
                                                                        onChange={e =>
                                                                            setShowOlderPolicies(e.target.checked)
                                                                        }
                                                                    />
                                                                    Show older policies
                                                                </label>
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                /* ROLLOVER → manual input */
                                                <input
                                                    type="text"
                                                    value={formData.previousPolicyNumber}
                                                    onChange={e => {
                                                        const value = e.target.value.toUpperCase();

                                                        setFormData(prev => ({
                                                            ...prev,
                                                            previousPolicyNumber: value
                                                        }));

                                                        setErrors(prev => ({
                                                            ...prev,
                                                            previousPolicyNumber:
                                                                validatePreviousPolicyNumber(value)
                                                        }));
                                                    }}
                                                    placeholder="Previous Policy Number"
                                                    className={
                                                        errors.previousPolicyNumber ? 'input-error' : ''
                                                    }
                                                />
                                            )}

                                            {errors.previousPolicyNumber && (
                                                <span className="field-error">
                                                    {errors.previousPolicyNumber}
                                                </span>
                                            )}
                                        </div>
                                    )}


                                </div>
                            </div>


                            {/* ================= ROLLOVER DOCUMENT UPLOAD ================= */}
                            {formData.typeOfBusiness === 'ROLLOVER' && (
                                <div className="add-prospect-form-group">
                                    <label className="form-label">
                                        Upload Previous Policy (PDF) <span style={{ color: '#888' }}>(Optional)</span>
                                    </label>

                                    <div
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            setIsDragging(true);
                                        }}
                                        onDragLeave={() => setIsDragging(false)}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            setIsDragging(false);

                                            const file = e.dataTransfer.files[0];
                                            if (!file || file.type !== 'application/pdf') return;

                                            setRolloverDoc(file);
                                            setRolloverPreviewUrl(URL.createObjectURL(file));
                                        }}
                                        style={{
                                            border: '2px dashed #aaa',
                                            padding: '20px',
                                            textAlign: 'center',
                                            background: isDragging ? '#f0f8ff' : '#fafafa',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() =>
                                            document.getElementById('rollover-upload').click()
                                        }
                                    >
                                        {rolloverDoc ? (
                                            <b>{rolloverDoc.name}</b>
                                        ) : (
                                            'Drag & drop PDF here or click to upload'
                                        )}
                                    </div>

                                    <input
                                        id="rollover-upload"
                                        type="file"
                                        accept="application/pdf"
                                        style={{ display: 'none' }}
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (!file) return;

                                            setRolloverDoc(file);
                                            setRolloverPreviewUrl(URL.createObjectURL(file));
                                        }}
                                    />

                                    {rolloverDoc && (
                                        <button
                                            type="button"
                                            className="preview-btn"
                                            onClick={() => setShowDocPreview(true)}
                                        >
                                            👁 Preview Document
                                        </button>
                                    )}

                                    {formData.rolloverPolicyDocPath && !rolloverDoc && (
                                        <button
                                            type="button"
                                            className="preview-btn"
                                            onClick={() => {
                                                setDocPreviewUrl(
                                                    `${API_BASE_URL}/${formData.rolloverPolicyDocPath}`
                                                );
                                                setShowDocPreview(true);
                                            }}
                                        >
                                            👁 Preview Document
                                        </button>
                                    )}
                                </div>
                            )}


                            {showDocPreview && (
                                <div className="modal-overlay">
                                    <div className="modal-content">
                                        <div className="modal-header">
                                            <h3>Document Preview</h3>
                                            <button
                                                className="close-btn"
                                                onClick={() => setShowDocPreview(false)}
                                            >
                                                ✖
                                            </button>
                                        </div>

                                        {/* <iframe
                                        src={docPreviewUrl}
                                        title="PDF Preview"
                                        width="100%"
                                        height="500px"
                                        style={{ border: 'none' }}
                                    /> */}

                                        {rolloverPreviewUrl && (
                                            <div style={{ marginTop: '10px' }}>
                                                <iframe
                                                    src={rolloverPreviewUrl}
                                                    title="PDF Preview"
                                                    width="100%"
                                                    height="300px"
                                                    style={{ border: '1px solid #ccc' }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            <br></br>

                            {formData.typeOfBusiness === 'ROLLOVER' && (
                                <div className="add-prospect-form-row-2cols">
                                    <div className="add-prospect-form-group" style={{ position: 'relative' }} ref={insurerRef}>
                                        <label className="form-label">Current Insurer</label>

                                        <input
                                            type="text"
                                            value={insurerInput}
                                            placeholder="Search insurer"
                                            onChange={e => {
                                                setInsurerInput(e.target.value);
                                                setShowInsurerDropdown(true);
                                                setFormData(prev => ({
                                                    ...prev,
                                                    currentInsurerId: '',
                                                    currentInsurerName: ''
                                                }));
                                            }}
                                            onFocus={() => setShowInsurerDropdown(true)}
                                        />

                                        {showInsurerDropdown && insurerSuggestions.length > 0 && (
                                            <ul className="dropdown">
                                                {insurerSuggestions.map(ins => (
                                                    <li
                                                        key={ins._id}
                                                        onMouseDown={() => {
                                                            setInsurerInput(ins.insurerName);
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                currentInsurerId: ins._id,
                                                                currentInsurerName: ins.insurerName
                                                            }));
                                                            setShowInsurerDropdown(false);
                                                        }}
                                                    >
                                                        <b>{ins.insurerName}</b>
                                                        <br />
                                                        <small>{ins.shortName}</small>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>




                                    <div className="add-prospect-form-group">
                                        <label className="form-label">Intermediary Details</label>

                                        <button
                                            type="button"
                                            className="intermediator-btn"
                                            onClick={() => setShowIntermediatorModal(true)}
                                        >
                                            {hasIntermediatorDetails
                                                ? '✏️ Edit Intermediary Details'
                                                : '+ Add Intermediary Details'}
                                        </button>

                                    </div>

                                </div>
                            )}


                            {showIntermediatorModal && (
                                <div className="modal-overlay">
                                    <div className="modal-content">

                                        <h3>Intermediary Details</h3>

                                        <input
                                            placeholder="Intermediary Name"
                                            value={intermediator.name}
                                            onChange={e =>
                                                setIntermediator(prev => ({ ...prev, name: e.target.value }))
                                            }
                                        />

                                        <input
                                            placeholder="IRDAI Number"
                                            value={intermediator.irdaiNumber}
                                            onChange={e =>
                                                setIntermediator(prev => ({ ...prev, irdaiNumber: e.target.value }))
                                            }
                                        />

                                        <input
                                            placeholder="Location"
                                            value={intermediator.location}
                                            onChange={e =>
                                                setIntermediator(prev => ({ ...prev, location: e.target.value }))
                                            }
                                        />

                                        <textarea
                                            placeholder="Remarks"
                                            value={intermediator.remarks}
                                            onChange={e =>
                                                setIntermediator(prev => ({ ...prev, remarks: e.target.value }))
                                            }
                                        />

                                        <div className="modal-actions">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        intermediatorDetails: intermediator
                                                    }));
                                                    setShowIntermediatorModal(false);
                                                }}
                                            >
                                                Save
                                            </button>


                                            <button
                                                type="button"
                                                className="cancel-btn"
                                                onClick={() => setShowIntermediatorModal(false)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ================= PREMIUM ================= */}
                            {/* <div className="form-group">
                            <label>Gross Premium *</label>
                            <input
                                type="number"
                                value={formData.grossPremium}
                                onChange={e =>
                                    setFormData(prev => ({
                                        ...prev,
                                        grossPremium: e.target.value
                                    }))
                                }
                                required
                            />
                        </div> */}


                            <br></br>
                            {/* ================= PREMIUM SIZE ================= */}
                            <div className='add-prospect-form-row-4'>
                                <div className="add-prospect-form-group">
                                    <label className="form-label form-label-required">
                                        Premium Amount
                                    </label>

                                    <div style={{ position: 'relative' }}>
                                        <span
                                            style={{
                                                position: 'absolute',
                                                left: '10px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                fontWeight: 600,
                                                color: '#555'
                                            }}
                                        >
                                            ₹
                                        </span>

                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            placeholder="Enter premium amount"
                                            value={premiumDisplay}
                                            onChange={(e) => {
                                                const rawValue = parseINR(e.target.value);

                                                if (!rawValue) {
                                                    setPremiumDisplay('');
                                                    setPremiumError('');
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        premiumSize: ''
                                                    }));
                                                    return;
                                                }

                                                const numericValue = Number(rawValue);

                                                // ❌ Max limit check
                                                if (numericValue > 100000000) {
                                                    setPremiumError(
                                                        'Maximum allowed premium is ₹10,00,00,000'
                                                    );
                                                    return;
                                                }

                                                // ✅ Valid value
                                                setPremiumError('');
                                                setPremiumDisplay(formatINR(numericValue));

                                                setFormData(prev => ({
                                                    ...prev,
                                                    premiumSize: numericValue
                                                }));
                                            }}
                                            style={{
                                                paddingLeft: '28px',
                                                borderColor: premiumError ? 'red' : undefined
                                            }}
                                            required
                                        />
                                    </div>

                                    {/* Inline error */}
                                    {premiumError && (
                                        <small className="field-error">
                                            {premiumError}
                                        </small>
                                    )}
                                </div>
                            </div>

                            {/* ================= DATES ================= */}
                            <div className='add-prospect-form-row-5'>
                                <div className="add-prospect-form-group">
                                    <label className="form-label form-label-required">Renewal Date</label>
                                    <input
                                        type="date"
                                        value={formData.tentativeStartDate}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                tentativeStartDate: e.target.value
                                            }))
                                        }
                                        required
                                    />

                                </div>

                                <div className="add-prospect-form-group">
                                    <label className="form-label form-label-required">First Follow-up Date</label>
                                    <input
                                        type="date"
                                        min={new Date().toISOString().split('T')[0]}
                                        value={formData.firstFollowUpDate}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                firstFollowUpDate: e.target.value
                                            }))
                                        }
                                        required
                                    />
                                </div>
                            </div>

                            {/* ================= REMARKS ================= */}
                            <div className='add-prospect-form-row-6'>
                                <div className="add-prospect-form-group full">
                                    <label className="form-label ">Remarks</label>
                                    <textarea
                                        value={formData.remarks}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                remarks: e.target.value
                                            }))
                                        }
                                    />
                                </div>
                            </div>

                            <button type="submit" className="save-btn">
                                Save Prospect
                            </button>
                        </form>
                    </section>
                </div>
            </div>
        </div >
    );
};

export default RetailPolicyProspect;
